import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuthState } from "../../auth/AuthStateProvider";
import { probeM365Reachability } from "../../shared/api/m365Probe";
import { logError, logEvent, scrubMessage } from "../../telemetry";
import { enrichModulesWithCredentials, fetchStudioSettings, StudioBadResponseError } from "./api";
import {
  clearAccessBlock,
  compareSnapshots,
  computeSnapshot,
  registerAccessGuard,
  resetAccessGuard,
  statusToReason,
  type AccessSnapshot,
} from "./accessGuard";
import type { AccessBlockReason, StudioSettingsData, StudioSettingsResponse } from "./types";

type LoadStatus = "idle" | "loading" | "ready" | "error";

interface AppConfigState {
  status: LoadStatus;
  data: StudioSettingsData | null;
  error: Error | null;
  /**
   * Set when a per-request access re-validation (accessGuard) detects the user
   * was revoked or their access changed mid-session. Takes precedence over the
   * load-state gates in TaskPane and drives the reload-on-Retry notice. Null when
   * access is current.
   */
  accessBlock: AccessBlockReason | null;
  /**
   * Microsoft 365 reachability from a live Graph probe. Null until the first probe
   * settles; consumers treat null as reachable so the dot never accuses Microsoft
   * 365 before it has been checked.
   */
  m365Connected: boolean | null;
  refresh: () => Promise<void>;
}

const AppConfigContext = createContext<AppConfigState | null>(null);

/**
 * AIVA-5972: auto-recovery backoff for a settings load that failed transiently.
 * After a few minutes idle the connection to the addin-api goes cold (the dev
 * backend scales to zero, APIM drops the idle connection, or the task-pane
 * webview is suspended and woken), so the load that runs on wake fails and the
 * surface latches into the "Unable to connect" notice — recoverable only by the
 * manual Retry. These re-run the load on a capped backoff so the connection
 * re-establishes on its own once the backend is reachable again.
 */
export const RETRY_BASE_MS = 5 * 1000;
export const RETRY_MAX_MS = 30 * 1000;

interface ProviderProps {
  readonly channel: string;
  readonly children: ReactNode;
  readonly initialSettings?: StudioSettingsResponse;
}

function claimsFromToken(token: string): Record<string, unknown> {
  try {
    const payload = token.split(".")[1];
    if (!payload) return {};
    const padded = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function emailFromToken(token: string): string | undefined {
  const c = claimsFromToken(token) as {
    upn?: string;
    preferred_username?: string;
    unique_name?: string;
    email?: string;
  };
  return c.upn || c.preferred_username || c.email || c.unique_name;
}

function tenantIdFromToken(token: string): string | undefined {
  const c = claimsFromToken(token) as { tid?: string };
  return c.tid ?? undefined;
}

export function AppConfigProvider({ channel, children, initialSettings }: ProviderProps) {
  const { graphToken, idToken, signedIn, refreshIdToken } = useAuthState();
  const [data, setData] = useState<StudioSettingsData | null>(
    initialSettings?.data ?? null,
  );
  const [status, setStatus] = useState<LoadStatus>(initialSettings ? "ready" : "idle");
  const [error, setError] = useState<Error | null>(null);
  const [accessBlock, setAccessBlock] = useState<AccessBlockReason | null>(null);
  const [m365Connected, setM365Connected] = useState<boolean | null>(null);

  // Snapshot of the access-gating fields captured on the last successful load.
  // The access guard re-fetches settings before each backend call and compares
  // against this to detect mid-session changes.
  const baselineRef = useRef<AccessSnapshot | null>(null);

  // Mirror volatile auth values into refs so `load` can read the latest
  // values without taking a dep on the changing string identities. Token
  // refreshes flip the underlying state every ~50 min — without this, every
  // refresh recreates `load`, retriggers the load effect, and re-fans out
  // one /settings + N /credentials calls.
  const graphTokenRef = useRef(graphToken);
  const idTokenRef = useRef(idToken);
  // The Studio settings / connector-credentials calls send the id token as
  // their bearer, so a 401 retry must refresh the *id* token, not the backend
  // access token — see refreshIdToken in AuthStateProvider.
  const refreshIdTokenRef = useRef(refreshIdToken);
  useEffect(() => {
    graphTokenRef.current = graphToken;
    idTokenRef.current = idToken;
    refreshIdTokenRef.current = refreshIdToken;
  }, [graphToken, idToken, refreshIdToken]);

  const inFlightRef = useRef<Promise<void> | null>(null);

  // Resolve the settings-call identity (email / tenant / bearer / refresh) from
  // the latest auth values. Shared by the initial load and the access guard's
  // re-validation so both target the same account.
  const resolveIdentity = useCallback(() => {
    const currentGraphToken = graphTokenRef.current;
    // The task pane holds no MSAL account object (V-08 — no browser token
    // client runs here), so the identity comes from the token's own claims.
    const email = (currentGraphToken ? emailFromToken(currentGraphToken) : undefined) || undefined;
    const tenantId = currentGraphToken ? tenantIdFromToken(currentGraphToken) : undefined;
    return {
      email,
      tenantId,
      bearer: idTokenRef.current ?? undefined,
      refresh: refreshIdTokenRef.current,
    };
  }, []);

  // Handle a settings-load failure. Extracted from load() so its own branching
  // (silent re-check, error coercion, log shaping) doesn't inflate load()'s
  // cognitive complexity. A silent background re-check keeps the working surface
  // and returns without touching status/error.
  const handleLoadError = useCallback(
    (err: unknown, opts?: { background?: boolean; silent?: boolean }) => {
      if (opts?.silent) return;
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      setStatus("error");
      // Emit one app-level event so the disconnected state is visible on the Logs
      // page (fetchWithRefresh logs the raw error.*; this marks what drove the UI),
      // carrying the HTTP status when present and whether it was the initial load
      // or a background self-heal retry.
      logError(
        "app.settings.load-failed",
        { type: e.name, scrubbedMessage: scrubMessage(e.message) },
        {
          backend: "addin",
          reason: opts?.background ? "background-retry" : "initial-load",
          ...(e instanceof StudioBadResponseError ? { statusCode: e.status } : {}),
        },
      );
    },
    [],
  );

  const load = useCallback(async (opts?: { background?: boolean; silent?: boolean }): Promise<void> => {
    // Concurrent-call guard: a second refresh() while one is mid-flight
    // returns the same promise instead of fanning out a fresh /settings +
    // N /credentials batch.
    if (inFlightRef.current) return inFlightRef.current;

    const run = (async () => {
      // A silent refresh (the focus/visibility re-check) keeps the current
      // surface up while it re-fetches in the background — flipping to "loading"
      // would flash the whole pane on every tab return. It updates data on
      // success and fails open (keeps the existing data) on error.
      if (!opts?.silent) {
        setStatus("loading");
        setError(null);
      }
      try {
        const { email, tenantId, bearer, refresh: interactiveRefresh } = resolveIdentity();
        // Background loads (the AIVA-5972 auto-retry timer / focus recovery) must
        // never open the auth dialog on a 401 — a repeating retry would flash the
        // silent-refresh dialog every cycle (the AIVA-5861 popup-loop class). Use
        // the dialog-free id-token refresh; a genuinely dead token is recovered by
        // the reactive 401 path on the user's next action instead.
        const refresh = opts?.background
          ? () => refreshIdTokenRef.current(false)
          : interactiveRefresh;
        // skipAccessCheck: the initial load establishes the baseline — there is
        // nothing to validate yet, and it must not recurse through the guard.
        const payload = await fetchStudioSettings(
          { channel, bearer, email, tenantId },
          { refresh, skipAccessCheck: true },
        );
        // Settings only returns {name, systemName, status}; the per-connector
        // Key Vault credentials come from a second endpoint, fanned out one
        // call per unique systemName found in modules[*].connectors[*]. The
        // merged authConfig + apiBaseUrl is bound onto each module's connector
        // entry. Failures fall back to env-driven getConnectionAuthConfig so a
        // single misconfigured connector can't keep the add-in from booting.
        const modules = await enrichModulesWithCredentials(
          payload.data.modules,
          { channel, bearer, email },
          { refresh, skipAccessCheck: true },
        );
        const finalData = { ...payload.data, modules };
        setData(finalData);
        setStatus("ready");
        // Establish the access-guard baseline and reset any prior block. A user
        // who is already non-active at load is blocked immediately (Retry reloads).
        baselineRef.current = computeSnapshot(finalData);
        clearAccessBlock();
        // Only an explicit non-active status blocks; a missing/empty status
        // (older payloads) is treated as active, mirroring normaliseSettings.
        // The exact status drives the notice copy (inactive/suspended/archived…).
        const status = (finalData.user.status || "active").toLowerCase();
        setAccessBlock(status !== "active" ? statusToReason(status) : null);
        logEvent("app.settings.loaded", "info");
      } catch (err) {
        handleLoadError(err, opts);
      }
    })();

    inFlightRef.current = run;
    try {
      await run;
    } finally {
      inFlightRef.current = null;
    }
  }, [channel, resolveIdentity, handleLoadError]);

  // Register the per-request access guard. Its validate() re-fetches settings
  // (skipping the guard to avoid recursion) and compares against the baseline
  // captured at load; onBlocked flips accessBlock so TaskPane shows the
  // reload-on-Retry notice. Transient failures fail open (return null) so a
  // backend hiccup can't lock a legitimately-entitled user out of every call.
  useEffect(() => {
    const validate = async (): Promise<AccessBlockReason | null> => {
      const baseline = baselineRef.current;
      if (!baseline) return null;
      try {
        const { email, tenantId, bearer } = resolveIdentity();
        // AIVA-5972: the access guard is a background preflight that runs before
        // every backend call, not the user's own action — it must never open the
        // auth dialog on a 401. A visible refresh here would flash a standalone
        // popup on desktop dialog-sign-in hosts (and a second one when the real
        // request then refreshes). Use the dialog-free id-token refresh; the
        // actual request drives the single user-facing refresh, and a genuinely
        // stale token here just fails open (return null) as before.
        const refresh = () => refreshIdTokenRef.current(false);
        const payload = await fetchStudioSettings(
          { channel, bearer, email, tenantId },
          { refresh, skipAccessCheck: true },
        );
        return compareSnapshots(baseline, computeSnapshot(payload.data));
      } catch (err) {
        // A purged/removed user now 404s the settings call — a definitive
        // "account gone" state, so block. Everything else (network, 5xx, timeout)
        // is potentially transient: fail open and let the request proceed.
        if (err instanceof StudioBadResponseError && err.status === 404) return "notfound";
        return null;
      }
    };
    registerAccessGuard({ validate, onBlocked: setAccessBlock });
    return () => resetAccessGuard();
  }, [channel, resolveIdentity]);

  // Trigger load only on the sign-in transition (or on first mount when
  // tokens were already restored from localStorage). Token-string refreshes
  // mid-session must not re-fire the cascade.
  const prevSignedInRef = useRef(false);
  useEffect(() => {
    if (initialSettings) return;
    if (signedIn && !prevSignedInRef.current) {
      prevSignedInRef.current = true;
      // load() has its own try/catch and never rejects; the .catch is a
      // belt-and-braces guard so the floating promise is properly handled.
      load().catch(() => undefined);
    } else if (!signedIn) {
      prevSignedInRef.current = false;
    }
  }, [signedIn, initialSettings, load]);

  // AIVA-5972: self-heal a transient load failure. TaskPane blocks the whole
  // surface with the "Unable to connect" notice whenever status is "error"
  // (AIVA-5817), and load() only fires on the sign-in transition — so a load
  // that failed because the connection was momentarily cold after idle strands
  // the user until they manually Retry. While in the error state, re-run the
  // load on a capped exponential backoff (the load's own in-flight guard dedupes
  // it against any concurrent refresh). A restored connection flips status back
  // to "ready" and this effect tears the timer down. focus/visibility trigger an
  // immediate retry too, but — as the auth provider notes — those events are
  // unreliable in embedded Office webviews, so the timer is the real backstop.
  const retryAttemptRef = useRef(0);
  // A 404 is the definitive "account not set up" state (AIVA-5721) shown with a
  // no-Retry notice, not a transient connection drop — don't poll it in a loop.
  const isTransientError =
    status === "error" &&
    !(error instanceof StudioBadResponseError && error.status === 404);
  useEffect(() => {
    if (!isTransientError) {
      // Reset the backoff once the load settles anywhere other than a transient
      // error (ready/idle/loading/404) so the next cold spell starts at the base.
      if (status !== "loading") retryAttemptRef.current = 0;
      return;
    }
    const attempt = retryAttemptRef.current;
    const delay = Math.min(RETRY_BASE_MS * 2 ** attempt, RETRY_MAX_MS);
    const retry = () => {
      retryAttemptRef.current = attempt + 1;
      load({ background: true }).catch(() => undefined);
    };
    const id = setTimeout(retry, delay);
    const onFocus = () => retry();
    const onVisibility = () => {
      if (document.visibilityState === "visible") retry();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearTimeout(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isTransientError, status, load]);

  // Reflect out-of-band Studio changes without a manual reload: re-check
  // settings when the add-in regains focus/visibility while signed in and
  // already loaded. This is what lets a re-granted module/persona reappear
  // after the user changed it in Studio and returned to the add-in — the KB
  // redirect (AIVA-7091) removes the tile on revocation, and this restores it on
  // re-grant. Silent + dialog-free so it never flashes the surface or pops the
  // auth dialog; a small throttle dedupes the focus+visibilitychange pair that
  // both fire on a tab return.
  const lastFocusRefreshRef = useRef(0);
  useEffect(() => {
    if (!signedIn || status !== "ready") return;
    const refresh = () => {
      const now = Date.now();
      if (now - lastFocusRefreshRef.current < 3000) return;
      lastFocusRefreshRef.current = now;
      load({ background: true, silent: true }).catch(() => undefined);
    };
    const onFocus = () => refresh();
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [signedIn, status, load]);

  // Re-probed on Graph-token rotation (when a previously-401ing probe should be
  // retried) and on the settings re-check's focus/visibility cycle, so the dot
  // recovers on its own. Same 3s throttle, since both events fire on a tab return.
  const lastProbeRef = useRef(0);
  useEffect(() => {
    if (!signedIn || !graphToken) {
      // Unknown, not unreachable — the dot must not blame Microsoft 365 for an
      // incomplete sign-in.
      setM365Connected(null);
      return;
    }
    let cancelled = false;
    const probe = (throttled: boolean) => {
      const now = Date.now();
      if (throttled && now - lastProbeRef.current < 3000) return;
      lastProbeRef.current = now;
      probeM365Reachability(graphToken)
        .then((reachable) => {
          if (!cancelled) setM365Connected(reachable);
        })
        .catch(() => {
          if (!cancelled) setM365Connected(false);
        });
    };
    probe(false);
    const onFocus = () => probe(true);
    const onVisibility = () => {
      if (document.visibilityState === "visible") probe(true);
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [signedIn, graphToken]);

  const value = useMemo<AppConfigState>(
    () => ({ status, data, error, accessBlock, m365Connected, refresh: load }),
    [status, data, error, accessBlock, m365Connected, load],
  );

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
}

export function useAppConfig(): AppConfigState {
  const ctx = useContext(AppConfigContext);
  if (!ctx) {
    throw new Error("useAppConfig must be used within AppConfigProvider");
  }
  return ctx;
}

/**
 * Returns the AppConfig state if a provider is mounted, or null otherwise.
 * Lets standalone Settings tabs render placeholder copy in storybook /
 * unit-test scenarios without forcing every caller to wrap with the
 * provider chain.
 */
export function useOptionalAppConfig(): AppConfigState | null {
  return useContext(AppConfigContext);
}
